import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  DollarSign, ShoppingBag, UtensilsCrossed, Users, Boxes, AlertTriangle, Clock, TrendingUp,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loading } from "@/components/dash/ui";

export default function Overview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  if (!stats) return <Loading />;

  const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="overview-page">
      <PageHeader
        title={`Good day, ${user?.name?.split(" ")[0] || "there"}`}
        subtitle="Here's how your restaurant is doing today."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Today's Revenue" value={fmt(stats.today_revenue)} hint="Updated live" />
        <StatCard icon={TrendingUp} label="Total Revenue" value={fmt(stats.total_revenue)} hint={`${stats.total_orders} orders`} accent="text-secondary" />
        <StatCard icon={ShoppingBag} label="Avg Order Value" value={fmt(stats.avg_order_value)} accent="text-primary" />
        <StatCard icon={Clock} label="Clocked In" value={stats.clocked_in || 0} hint="Staff on shift" accent="text-secondary" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Revenue · last 14 days</h3>
            <Link to="/dashboard/sales" className="text-sm text-primary hover:underline">View report</Link>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.sales_series || []}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C86A51" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C86A51" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#736B5E" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#736B5E" }} tickFormatter={(v) => `$${v}`} width={48} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: "1px solid #EBE6DE" }} />
                <Area type="monotone" dataKey="amount" stroke="#C86A51" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-4">
          <Link to="/dashboard/orders" className="block"><StatCard icon={ShoppingBag} label="Pending Orders" value={stats.orders_pending || 0} hint={stats.orders_pending ? "Tap to manage" : "All caught up"} accent={stats.orders_pending ? "text-primary" : "text-secondary"} /></Link>
          <StatCard icon={UtensilsCrossed} label="Menu Items" value={stats.menu_count || 0} />
          <StatCard icon={Boxes} label="Low Stock Items" value={stats.low_stock || 0} hint={stats.low_stock ? "Needs attention" : "All good"} accent={stats.low_stock ? "text-primary" : "text-secondary"} />
          <StatCard icon={Users} label="Customers" value={stats.customer_count || 0} accent="text-secondary" />
        </div>
      </div>

      {stats.low_stock > 0 && (
        <div className="mt-6 bg-accent/40 border border-accent rounded-xl p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium">{stats.low_stock} item(s) are running low</p>
            <p className="text-sm text-muted-foreground">Check your inventory and reorder before you run out.</p>
            <Link to="/dashboard/inventory" className="text-sm text-primary font-medium hover:underline mt-1 inline-block">Go to inventory →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
